import { useEffect, useState } from "react";
import { PracticeHubScreen } from "../../screens/practice/PracticeHubScreen";
import { ScreenState } from "../../components/ScreenState";
import { TabBar } from "../../components/TabBar";
import { OfflineNote } from "../../components/OfflineNote";
import { ExamSwitcher } from "../../components/ExamSwitcher";
import { useCachedAsync } from "../../lib/useCachedAsync";
import { downloadedSubjectIds } from "../../lib/offlinePacks";
import { fetchMe } from "../../api/me";

/**
 * The practice hub, from the student's real subjects.
 *
 * It listed four hardcoded names, so the chips did not match what the student
 * had chosen and the id needed to start a session did not exist at all — which
 * is why the year picker could not have worked even with a handler.
 *
 * Offline it opens from the saved profile and offers only the subjects that are
 * downloaded: those are the ones that can actually be practised with no signal.
 */
export function PracticeHubRoute() {
  const { data, loading, error, reload, staleSince } = useCachedAsync("me", fetchMe);
  const [downloaded, setDownloaded] = useState<Set<string> | null>(null);

  // Downloads are per exam, so the list is re-read when the profile changes.
  const exam = data?.activeExam ?? null;
  useEffect(() => {
    void downloadedSubjectIds().then(setDownloaded);
  }, [exam]);

  // Showing saved data, or the phone itself says there is no network: in
  // either case only downloaded subjects can actually be practised.
  const offline = staleSince !== null || (typeof navigator !== "undefined" && !navigator.onLine);
  const subjects = (data?.subjects ?? [])
    .filter((subject) => !offline || downloaded?.has(subject.subjectId))
    .map((subject) => ({ id: subject.subjectId, name: subject.name }));

  return (
    // Wait for the downloads list: offline, offering a subject that cannot be
    // practised would lead straight to an error.
    <ScreenState
      // A quick local read; waiting for it every time means the list never
      // flickers from every subject down to the downloaded ones.
      loading={loading || downloaded === null}
      error={error}
      onRetry={reload}
      footer={<TabBar />}
      header={<ExamSwitcher exams={data?.exams} active={data?.activeExam} />}
    >
      <OfflineNote since={staleSince} />
      {data ? (
        offline && subjects.length === 0 ? (
          <div className="app-column">
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-5 text-center">
              <p className="m-0 font-[family-name:var(--font-display)] text-[18px] font-bold">
                Nothing downloaded yet
              </p>
              <p className="m-0 max-w-[300px] text-[13.5px] leading-[1.5] text-text-muted">
                When you have signal, open Downloads in your profile and download a subject. You can
                then practise it anywhere.
              </p>
            </div>
            <TabBar />
          </div>
        ) : (
          <PracticeHubScreen
            // A new profile is a new subject list: start from its first chip.
            key={exam ?? "none"}
            subjects={subjects}
            dueForReview={0}
            // The mock is a JAMB CBT; WAEC is written papers, practised per subject.
            showMock={exam !== "WAEC"}
          />
        )
      ) : null}
    </ScreenState>
  );
}
